import {
  fetchWidgetConfig,
  sendChatMessage,
  fetchHistory,
  sendClearHistoryBeacon,
  WidgetApiError,
} from './api'
import { getSessionId } from './session'
import { registerTabOpen, registerTabClosed } from './tabs'
import { mountWidget } from './ui'
import type { ChatMessage, WidgetEmbedConfig } from './types'

/**
 * Ponto de entrada — o que roda quando o <script> é carregado no site do
 * cliente. document.currentScript só é confiável durante a execução SÍNCRONA
 * do próprio script (mesmo com `defer`), por isso é capturado na primeira
 * linha, antes de qualquer await.
 */
const scriptEl = document.currentScript as HTMLScriptElement | null

function readEmbedConfig(script: HTMLScriptElement | null): WidgetEmbedConfig | null {
  const tenantId = script?.dataset.tenantId
  const apiUrl = script?.dataset.apiUrl
  if (!tenantId || !apiUrl) {
    console.error(
      '[pilar-widget] data-tenant-id e data-api-url são obrigatórios no <script>. Exemplo:\n' +
        '<script src="widget.js" data-tenant-id="frankie" data-api-url="https://api.exemplo.com" defer></script>'
    )
    return null
  }
  return { tenantId, apiUrl: apiUrl.replace(/\/$/, '') }
}

async function init(): Promise<void> {
  const embed = readEmbedConfig(scriptEl)
  if (!embed) return

  let config
  try {
    config = await fetchWidgetConfig(embed.apiUrl, embed.tenantId)
  } catch (error) {
    console.error('[pilar-widget] falha ao buscar config do tenant', error)
    return
  }

  const host = document.createElement('div')
  host.style.all = 'initial'
  document.body.appendChild(host)
  const shadowRoot = host.attachShadow({ mode: 'open' })

  const sessionId = getSessionId(embed.tenantId)

  // Best-effort: se esta for a última aba do widget neste site a fechar,
  // avisa o backend pra encurtar o TTL do histórico (ver src/tabs.ts e
  // pilar-agent/src/channels/web/history.ts pro porquê não é uma garantia).
  registerTabOpen(embed.tenantId)
  window.addEventListener('pagehide', () => {
    if (registerTabClosed(embed.tenantId)) {
      sendClearHistoryBeacon(embed.apiUrl, embed.tenantId, sessionId)
    }
  })

  let history: ChatMessage[] = []
  try {
    history = await fetchHistory(embed.apiUrl, embed.tenantId, sessionId)
  } catch (error) {
    console.error('[pilar-widget] falha ao restaurar histórico (segue sem ele)', error)
  }

  const ui = mountWidget(shadowRoot, config, history, async (text) => {
    ui.addMessage('user', text)
    ui.clearInput()
    ui.showTyping()
    try {
      const reply = await sendChatMessage(embed.apiUrl, embed.tenantId, sessionId, text)
      ui.hideTyping()
      ui.addMessage('assistant', reply)
    } catch (error) {
      ui.hideTyping()
      const rateLimited = error instanceof WidgetApiError && error.status === 429
      ui.showError(
        rateLimited
          ? 'Muitas mensagens em pouco tempo. Tente de novo em instantes.'
          : 'Não consegui responder agora. Tente de novo em instantes.'
      )
    }
  })
}

void init()
